/**
 * @component feed-filters
 * @css feed-filters.css
 * @primitives btn btn-secondary
 */
class FeedFilters extends HTMLElement {
  constructor() {
    super();
    this.filters = null;
    this.currentUser = null;
    this.isLoading = true;
    this.isSaving = false;
    this.error = null;
    this._onFiltersChanged = this._onFiltersChanged.bind(this);
  }

  connectedCallback() {
    this.render();
    this.loadFilters().then(() => {
      this.attachEventListeners();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('feed-filters-changed', this._onFiltersChanged);
  }

  _onFiltersChanged(event) {
    if (event.detail?.source === this) {
      return;
    }
    this.loadFilters();
  }

  async loadFilters() {
    try {
      this.isLoading = true;
      this.error = null;
      this.render();

      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated) {
        this.filters = null;
        return;
      }

      this.currentUser = authData.user;
      this.filters = await window.API.feedFilters.get(this.currentUser.id);
    } catch (err) {
      window.logger.error('Failed to load feed filters', err, { component: 'feed-filters' });
      this.error = err.message || 'Failed to load feed filters';
    } finally {
      this.isLoading = false;
      this.render();
      this.attachEventListeners();
    }
  }

  render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div class="feed-filters-panel">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading filters...</div>
          </div>
        </div>
      `;
      return;
    }

    if (this.error) {
      this.innerHTML = `
        <div class="feed-filters-panel">
          <div class="error-state">
            <p>${this.error}</p>
            <button type="button" class="btn btn-secondary retry-btn">Try again</button>
          </div>
        </div>
      `;
      return;
    }

    if (!this.filters) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = `
      <div class="feed-filters-panel">
        <div class="filter-group">
          <span class="filter-group-label">Sources</span>
          <div class="filter-chips">
            ${this.renderChip('feed_show_rss', 'RSS imports')}
            ${this.renderChip('feed_show_facebook', 'Facebook imports')}
            ${this.renderChip('feed_show_memory_share', 'Memory shares')}
          </div>
        </div>

        <div class="filter-group">
          <span class="filter-group-label">Groups</span>
          <div class="filter-chips">
            ${this.renderChip('feed_show_groups', 'Group posts')}
          </div>
        </div>

        <div class="filter-group">
          <span class="filter-group-label">Post types</span>
          <div class="filter-chips">
            ${this.renderChip('feed_show_text', 'Text')}
            ${this.renderChip('feed_show_mood', 'Mood')}
            ${this.renderChip('feed_show_music', 'Music')}
            ${this.renderChip('feed_show_poll', 'Polls')}
          </div>
        </div>

        ${this.isSaving ? '<p class="feed-filters-saving">Saving…</p>' : ''}
      </div>
    `;
  }

  renderChip(field, label) {
    const enabled = Boolean(this.filters[field]);
    return `
      <button type="button"
              class="filter-chip ${enabled ? 'is-on' : 'is-off'}"
              data-field="${field}"
              aria-pressed="${enabled ? 'true' : 'false'}"
              ${this.isSaving ? 'disabled' : ''}>
        ${label}
      </button>
    `;
  }

  attachEventListeners() {
    document.addEventListener('feed-filters-changed', this._onFiltersChanged);

    this.querySelector('.retry-btn')?.addEventListener('click', () => {
      this.loadFilters();
    });

    this.querySelectorAll('.filter-chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        this.handleChipToggle(chip);
      });
    });
  }

  async handleChipToggle(chip) {
    if (this.isSaving || !this.currentUser || !this.filters) {
      return;
    }

    const field = chip.dataset.field;
    const nextValue = !this.filters[field];

    const previous = this.filters[field];
    this.filters[field] = nextValue;
    this.isSaving = true;
    this.render();
    this.attachEventListeners();

    try {
      const updated = await window.API.feedFilters.update(this.currentUser.id, {
        [field]: nextValue
      });
      this.filters = updated;
      document.dispatchEvent(new CustomEvent('feed-filters-changed', {
        detail: { source: this }
      }));
    } catch (err) {
      window.logger.error('Failed to update feed filter', err, { component: 'feed-filters', field });
      this.filters[field] = previous;
      this.error = err.message || 'Failed to save filter';
    } finally {
      this.isSaving = false;
      this.render();
      this.attachEventListeners();
    }
  }
}

customElements.define('feed-filters', FeedFilters);
